import { useEffect, useRef, useState } from 'react';
import { Aviso } from './ui';

/**
 * Leitura de QR Code pela câmera (item 6 e item 18 do briefing).
 *
 * A etiqueta carrega SÓ o código da amostra — não URL, não token
 * (`etiqueta.service.ts`). Isso significa que ler a etiqueta com a câmera
 * nativa do celular não leva a lugar nenhum: quem transforma o código em
 * tela é este leitor, dentro do sistema, com sessão.
 *
 * Dois caminhos, nesta ordem:
 *
 * 1. `BarcodeDetector`, quando o navegador tem. É nativo, roda fora da
 *    linha principal e não custa nada em bateria. O Chrome do Android — que
 *    é o que o laboratório usa — tem.
 * 2. `jsQR` sobre um quadro copiado num canvas. Funciona em qualquer
 *    navegador. É mais lento, então roda a cada ~150 ms em vez de a cada
 *    quadro, o que é sobra para um adesivo parado na mão.
 *
 * A câmera exige HTTPS. Em homologação e produção há certificado; em
 * desenvolvimento, `localhost` conta como origem segura. Se mesmo assim não
 * houver câmera, o componente não vira beco sem saída: cai para digitação.
 */

interface DetectorCodigo {
  detect(fonte: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
interface JanelaComDetector {
  BarcodeDetector?: new (o: { formats: string[] }) => DetectorCodigo;
}

/**
 * Entrega ou não o código `lido` (já sem espaços nas pontas), sabendo o
 * `ultimo` entregue. Um QR fica no campo de visão por vários quadros; sem
 * trava a tela navegaria dez vezes para a mesma amostra. A trava é «já leu
 * ESTE código», e não «já leu»: quem usa o leitor pode ficar na mesma tela
 * depois de uma leitura (a busca do topo avisa «Nenhuma amostra com o código
 * …» na própria janela), e aí a próxima etiqueta tem de ser lida (Ruling
 * R26). Função pura e exportada: o 295.14 a testa sem câmera.
 */
export function deveEntregar(ultimo: string | null, lido: string): boolean {
  return lido !== '' && lido !== ultimo;
}

export function LeitorQr({
  aoLer, aoCancelar,
}: { aoLer(codigo: string): void; aoCancelar(): void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  /* O último código entregue pela câmera. Mora fora do efeito: quando o
     `aoLer` muda, a câmera reabre, mas o QR que está parado no quadro
     continua entregue uma vez só. */
  const ultimo = useRef<string | null>(null);

  useEffect(() => {
    let parar = false;
    let fluxo: MediaStream | null = null;
    let timer: number | undefined;

    const entregar = (codigo: string) => {
      /* Uma detecção que estava em voo quando o leitor desmontou (a janela
         fechada) não chega ao `aoLer`: senão a tela navegaria depois de a
         pessoa ter desistido. */
      if (parar) return;
      const limpo = codigo.trim();
      if (!deveEntregar(ultimo.current, limpo)) return;
      ultimo.current = limpo;
      aoLer(limpo);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErro('Este navegador não dá acesso à câmera. Digite o código da amostra.');
        return;
      }
      try {
        fluxo = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch (e) {
        const nome = (e as Error).name;
        setErro(nome === 'NotAllowedError'
          ? 'Permissão de câmera negada. Libere nas configurações do navegador, ou digite o código.'
          : 'Não consegui abrir a câmera. Digite o código da amostra.');
        return;
      }
      if (parar) { fluxo.getTracks().forEach((t) => t.stop()); return; }

      const v = video.current;
      if (!v) return;
      v.srcObject = fluxo;
      // `playsInline` está no JSX; sem ele o iOS abre o vídeo em tela cheia
      // e o leitor some por baixo do reprodutor do sistema.
      await v.play().catch(() => undefined);

      const Detector = (window as unknown as JanelaComDetector).BarcodeDetector;
      if (Detector) {
        const det = new Detector({ formats: ['qr_code'] });
        /* O laço segue depois de uma leitura: a mesma etiqueta não é
           entregue de novo (`deveEntregar`), e a próxima é. */
        const quadro = async () => {
          if (parar) return;
          try {
            const achados = await det.detect(v);
            if (achados[0]?.rawValue) entregar(achados[0].rawValue);
          } catch { /* quadro ruim: tenta o próximo */ }
          timer = window.setTimeout(quadro, 120);
        };
        quadro();
        return;
      }

      const { default: jsQR } = await import('jsqr');
      const tela = document.createElement('canvas');
      const ctx = tela.getContext('2d', { willReadFrequently: true });
      const quadro = () => {
        if (parar || !ctx) return;
        const l = v.videoWidth, a = v.videoHeight;
        if (l && a) {
          // Metade da resolução: o QR de uma etiqueta de 40 mm continua
          // legível e o custo por quadro cai a um quarto.
          tela.width = Math.round(l / 2); tela.height = Math.round(a / 2);
          ctx.drawImage(v, 0, 0, tela.width, tela.height);
          const img = ctx.getImageData(0, 0, tela.width, tela.height);
          const achado = jsQR(img.data, img.width, img.height, {
            inversionAttempts: 'dontInvert',
          });
          if (achado?.data) entregar(achado.data);
        }
        timer = window.setTimeout(quadro, 150);
      };
      quadro();
    })();

    return () => {
      parar = true;
      if (timer) clearTimeout(timer);
      // Soltar a câmera é obrigação, não cortesia: sem isto a luz do
      // aparelho fica acesa até o navegador ser fechado.
      fluxo?.getTracks().forEach((t) => t.stop());
    };
  }, [aoLer]);

  return (
    <div>
      {erro
        ? <Aviso tipo="atencao" titulo={erro} />
        : (
          <div className="leitor">
            <video ref={video} muted playsInline aria-label="Câmera para leitura do QR Code" />
            <div className="mira" aria-hidden="true" />
          </div>
        )}

      <form
        style={{ marginTop: '.8rem' }}
        onSubmit={(e) => { e.preventDefault(); if (manual.trim()) aoLer(manual.trim()); }}
      >
        <label className="campo">
          <span className="rotulo">Ou digite o código da amostra</span>
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value.toUpperCase())}
            placeholder="AM-2026-00001"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
          />
        </label>
        <div className="linha">
          <button type="submit" className="botao" disabled={!manual.trim()}>Buscar</button>
          <button type="button" className="botao secundario" onClick={aoCancelar}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}
